import React from 'react';
import { createRoot } from 'react-dom/client';
import { ConvexProvider, ConvexReactClient } from "convex/react";
import { ConvexAuthProvider } from "@convex-dev/auth/react";
import * as Sentry from "@sentry/react";
import ErrorBoundary from './components/ErrorBoundary';
import App from './App.tsx';
import './index.css';

const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
const isProd = import.meta.env.PROD;
if (sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: isProd ? "production" : "development",
    release: import.meta.env.VITE_APP_VERSION || "0.0.0",
    // Session Replay and performance tracing are heavy: Replay records the whole
    // DOM continuously (buffer mode, via a document-wide MutationObserver) and
    // tracing instruments every interaction. In dev they make the entire app
    // sluggish for no real benefit, so we only enable them in production. Plain
    // error + feedback capture stays on everywhere (cheap).
    integrations: [
      Sentry.feedbackIntegration({ autoInject: false, colorScheme: "system" }),
      ...(isProd
        ? [
            Sentry.browserTracingIntegration(),
            Sentry.replayIntegration({ maskAllText: false, blockAllMedia: false }),
          ]
        : []),
    ],
    tracesSampleRate: isProd ? 0.2 : 0,
    replaysSessionSampleRate: 0,
    replaysOnErrorSampleRate: isProd ? 1.0 : 0,
  });
}

const convexUrl = import.meta.env.VITE_CONVEX_URL;
if (!convexUrl) {
  console.error("VITE_CONVEX_URL is not set. Please run 'npx convex dev' or set it in .env");
}

const convex = new ConvexReactClient(convexUrl || "http://localhost:3000"); // Fallback to avoid crash if not set, though it won't work

createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <ConvexAuthProvider client={convex} shouldHandleCode={false}>
      <ErrorBoundary>
        <App />
      </ErrorBoundary>
    </ConvexAuthProvider>
  </React.StrictMode>
);
